import { getRouteApi } from '@tanstack/react-router'
import { useCallback } from 'react'
import { z } from 'zod'

import { StoryList, type StoryFilters } from '@/features/story/StoryList'

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined)
/** `?view=bookmarked` shows only bookmarked stories; `?sort=bookmarked` puts them first. */
export const storiesSearchSchema = z.object({
  status: lenient(z.enum(['ready', 'generating', 'failed', 'archived', 'all'])),
  view: lenient(z.enum(['all', 'bookmarked'])),
  sort: lenient(z.enum(['newest', 'bookmarked'])),
  page: lenient(z.coerce.number().int().min(1)),
  pageSize: lenient(z.coerce.number().int().min(1).max(100)),
})
export type StoriesSearch = z.infer<typeof storiesSearchSchema>
export function validateStoriesSearch(input: Record<string, unknown>): StoriesSearch { return storiesSearchSchema.parse(input) }
const route = getRouteApi('/stories')
export function StoriesIndexPage() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const onFiltersChange = useCallback(
    (next: StoryFilters) =>
      void navigate({
        search: (prev) => ({
          ...prev,
          view: next.view === 'bookmarked' ? 'bookmarked' : undefined,
          sort: next.sort === 'bookmarked' ? 'bookmarked' : undefined,
          page: undefined,
        }),
        replace: true,
      }),
    [navigate],
  )
  const onPageChange = useCallback(
    (nextPage: number) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          page: nextPage > 1 ? nextPage : undefined,
        }),
        replace: true,
      })
    },
    [navigate],
  )
  return (
    <StoryList
      status={search.status}
      view={search.view}
      sort={search.sort}
      page={search.page}
      pageSize={search.pageSize}
      onFiltersChange={onFiltersChange}
      onPageChange={onPageChange}
    />
  )
}
